const NOTIFICATION_ITEM_KEYS = new Set([
  'id',
  'eventId',
  'type',
  'campaignId',
  'mailingId',
  'intent',
  'prospectId',
  'schemaVersion',
  'submittedAt',
  'createdAt',
  'readAt',
  'unread',
  'propertyAddress',
  'propertyCity',
]);

const NOTIFICATION_FORBIDDEN = new Set([
  'email',
  'phone',
  'token',
  'tokenHash',
  'fingerprint',
  'contact',
  'fullName',
  'ownerName',
  'recipientEmail',
  'recipientUid',
  'path',
]);

const MARK_READ_RESULT_KEYS = new Set([
  'id',
  'ok',
  'alreadyRead',
  'readAt',
  'reason',
]);

const INTENT_SET = new Set([
  'profitability_guide',
  'property_value',
  'property_sale',
  'legacy_owner_response',
]);

function optionalString(value) {
  return typeof value === 'string' && value.trim() ? value : null;
}

function optionalIso(value) {
  if (value == null) return null;
  return typeof value === 'string' ? value : null;
}

/** Key-only deny list — display string values may contain forbidden substrings. */
function assertNoForbiddenKeys(obj, forbidden, label) {
  for (const key of forbidden) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      throw new TypeError(`${label} leaked forbidden field: ${key}`);
    }
  }
}

function parseNotificationItem(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new TypeError('Invalid notification list item');
  }
  for (const key of Object.keys(raw)) {
    if (!NOTIFICATION_ITEM_KEYS.has(key)) {
      throw new TypeError(`Notification list item leaked field: ${key}`);
    }
  }
  assertNoForbiddenKeys(raw, NOTIFICATION_FORBIDDEN, 'Notification list item');

  const intentRaw = optionalString(raw.intent);
  const intent = intentRaw && INTENT_SET.has(intentRaw) ? intentRaw : intentRaw;
  const readAt = optionalIso(raw.readAt);
  const unread = raw.unread === true || (raw.unread !== false && readAt == null);

  return {
    id: optionalString(raw.id),
    eventId: optionalString(raw.eventId),
    type: optionalString(raw.type),
    campaignId: optionalString(raw.campaignId),
    mailingId: optionalString(raw.mailingId),
    intent,
    prospectId: optionalString(raw.prospectId),
    schemaVersion: Number.isInteger(raw.schemaVersion) ? raw.schemaVersion : null,
    submittedAt: optionalIso(raw.submittedAt),
    createdAt: optionalIso(raw.createdAt),
    readAt,
    unread,
    propertyAddress: optionalString(raw.propertyAddress),
    propertyCity: optionalString(raw.propertyCity),
  };
}

function parseMarkReadResult(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new TypeError('Invalid mark-read result item');
  }
  for (const key of Object.keys(raw)) {
    if (!MARK_READ_RESULT_KEYS.has(key)) {
      throw new TypeError(`Mark-read result leaked field: ${key}`);
    }
  }
  assertNoForbiddenKeys(raw, NOTIFICATION_FORBIDDEN, 'Mark-read result');
  return {
    id: optionalString(raw.id),
    ok: raw.ok === true,
    alreadyRead: raw.alreadyRead === true,
    readAt: optionalIso(raw.readAt),
    reason: optionalString(raw.reason),
  };
}

/**
 * @param {unknown} data
 */
export function parseListAdminProspectResponseNotifications(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new TypeError('Invalid notifications list DTO');
  }
  const items = Array.isArray(data.items) ? data.items.map(parseNotificationItem) : [];
  return {
    items,
    nextPageToken: typeof data.nextPageToken === 'string' ? data.nextPageToken : null,
    configured: data.configured !== false,
    configurationStatus:
      typeof data.configurationStatus === 'string' ? data.configurationStatus : null,
  };
}

/**
 * @param {unknown} data
 */
export function parseGetAdminProspectResponseNotificationUnreadCount(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new TypeError('Invalid unread-count DTO');
  }
  const count = data.unreadCount;
  return {
    unreadCount: Number.isInteger(count) && count >= 0 ? count : 0,
    configured: data.configured !== false,
    configurationStatus:
      typeof data.configurationStatus === 'string' ? data.configurationStatus : null,
  };
}

/**
 * @param {unknown} data
 */
export function parseMarkAdminProspectResponseNotificationsRead(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new TypeError('Invalid mark-read DTO');
  }
  const results = Array.isArray(data.results)
    ? data.results.map(parseMarkReadResult)
    : [];
  return { results };
}

/**
 * @param {unknown} data
 */
export function parseRecoverAdminProspectResponseNotifications(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new TypeError('Invalid recover DTO');
  }
  return {
    scanned: Number.isInteger(data.scanned) ? data.scanned : 0,
    wouldDeliver: Number.isInteger(data.wouldDeliver) ? data.wouldDeliver : 0,
    delivered: Number.isInteger(data.delivered) ? data.delivered : 0,
    pendingUnconfigured: Number.isInteger(data.pendingUnconfigured)
      ? data.pendingUnconfigured
      : 0,
    skippedBeforeActivation: Number.isInteger(data.skippedBeforeActivation)
      ? data.skippedBeforeActivation
      : 0,
    failed: Number.isInteger(data.failed) ? data.failed : 0,
    dryRun: data.dryRun !== false,
    configured: data.configured !== false,
    nextPageToken: typeof data.nextPageToken === 'string' ? data.nextPageToken : null,
  };
}
